// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CtsGantryGridSourceEType } from '../enum/cts-gantry-grid-source-type';
import { CtsGantryFunctionalUnitCheck } from '../interfaces/cts-gantry-functional-unit-check';
import { CtsGantryFunctionalUnitData } from '../interfaces/cts-gantry-functional-unit-data';
import { CtsGantryInitEvent } from '../interfaces/cts-gantry-init-event';
import { CtsGantryCacheModel } from './cts-gantry-cache-model';
import { CtsGantryFnUnitSelectionModel } from './cts-gantry-fn-unit-selection-model';

export class CtsGantryEventSelectionModel {
  public ctsEvent: {
    first: CtsGantryInitEvent | undefined;
    second: CtsGantryInitEvent | undefined;
  } = {
    first: undefined,
    second: undefined,
  };

  public fUnitData = new CtsGantryFnUnitSelectionModel();
  public detailSource: CtsGantryGridSourceEType | undefined;
  public detailEvent: CtsGantryInitEvent | undefined;
  public detailFUnitData: CtsGantryFunctionalUnitData | undefined;

  private readonly fUnitCache =
    new CtsGantryCacheModel<CtsGantryFunctionalUnitData>();

  public updateDetailEvent(ctsEvent: CtsGantryInitEvent): void {
    this.detailEvent = ctsEvent;
    if (this.detailSource === CtsGantryGridSourceEType.FIRST) {
      this.ctsEvent.first = this.detailEvent;
    } else if (this.detailSource === CtsGantryGridSourceEType.SECOND) {
      this.ctsEvent.second = this.detailEvent;
    }
  }

  public updateDetailFunctionalUnitData = (
    data: CtsGantryFunctionalUnitData,
  ): void => {
    this.detailFUnitData = data;
    if (this.detailSource === CtsGantryGridSourceEType.FIRST) {
      this.fUnitData.updateFirst(data);
    } else if (this.detailSource === CtsGantryGridSourceEType.SECOND) {
      this.fUnitData.updateSecond(data);
    }
  };

  public cacheFunctionalUnitData(
    eventSource: string,
    fUnitData: CtsGantryFunctionalUnitData,
  ): void {
    if (eventSource) {
      this.fUnitCache.cacheData(eventSource, fUnitData);
    }
  }

  public hasFunctionalUnitData(eventSource: string): boolean {
    return this.fUnitCache.hasData(eventSource);
  }

  public getCachedFunctionalUnitData(
    eventSource: string,
  ): CtsGantryFunctionalUnitData | undefined {
    return this.fUnitCache.getCachedData(eventSource);
  }

  public readonly update1stFunctionalData = (
    fUnitData: CtsGantryFunctionalUnitData,
  ): void => {
    this.updateFUnitAndCache(fUnitData, this.fUnitData.updateFirst);
  };

  public readonly update2ndFunctionalData = (
    fUnitData: CtsGantryFunctionalUnitData,
  ): void => {
    this.updateFUnitAndCache(fUnitData, this.fUnitData.updateSecond);
  };

  public readonly updateDetailsFunctionalData = (
    fUnitData: CtsGantryFunctionalUnitData,
  ): void => {
    this.updateFUnitAndCache(fUnitData, this.updateDetailFunctionalUnitData);
  };

  private readonly updateFUnitAndCache = (
    fUnitData: CtsGantryFunctionalUnitData,
    callback: (fUnitData: CtsGantryFunctionalUnitData) => void,
  ): void => {
    if (fUnitData) {
      const units = fUnitData.functionalUnits;
      if (units) {
        units.names?.sort((a, b) => a.localeCompare(b));
        units.data?.sort((a, b) => {
          const aName = a.functionalUnit ?? '';
          const bName = b.functionalUnit ?? '';
          return aName.localeCompare(bName);
        });
      }
      this.cacheFunctionalUnitData(fUnitData.eventSource, fUnitData);
      callback(fUnitData);
    }
  };

  public getDetailFUnitDataCheckList(): CtsGantryFunctionalUnitCheck[] {
    let result: CtsGantryFunctionalUnitCheck[] = [];
    if (this.detailFUnitData) {
      result = this.detailFUnitData.functionalUnits.data.map((fUnit) => ({
        fUnit,
        isInGrid: true,
      }));
    }

    return result;
  }
}
